// ─── ActivitySyncService — sync local activities to Supabase ────────────────────
import { supabase } from "../supabase";
import type { ActivityStats, GpsPoint, LocalActivity, UnitPref } from "./types";
import { LocalActivityStorage } from "./LocalActivityStorage";

function getDeviceId(): string {
  let id = localStorage.getItem("device_id");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("device_id", id); }
  return id;
}

export interface SyncResult {
  success: boolean;
  workoutId?: string;
  error?: string;
  alreadySynced?: boolean;
}

export async function syncActivity(
  activity: LocalActivity,
  userId?: string,
): Promise<SyncResult> {
  if (LocalActivityStorage.isSynced(activity.id)) {
    return { success: true, alreadySynced: true };
  }

  const s = activity.stats;
  const deviceId = getDeviceId();
  const startedAt = new Date(activity.startedAt).toISOString();

  const workoutPayload = {
    device_id: userId ? null : deviceId,
    user_id: userId ?? null,
    date: startedAt.slice(0, 10),
    started_at: startedAt,
    ended_at: new Date(activity.endedAt).toISOString(),
    duration_secs: s.totalSecs,
    moving_secs: s.movingSecs,
    paused_secs: s.pausedSecs,
    steps: s.steps,
    distance_km: Math.round((s.distance / 1000) * 1000) / 1000,
    avg_speed_kmh: Math.round((s.avgSpeed * 3.6) * 100) / 100,
    max_speed_kmh: Math.round((s.maxSpeed * 3.6) * 100) / 100,
    avg_pace_sec_per_km: Math.round(s.avgPace),
    calories: Math.round(s.calories * 100) / 100,
    elevation_gain_m: Math.round(s.elevationGain * 100) / 100,
    elevation_loss_m: Math.round(s.elevationLoss * 100) / 100,
    min_elevation_m: s.minElevation !== null ? Math.round(s.minElevation * 100) / 100 : null,
    max_elevation_m: s.maxElevation !== null ? Math.round(s.maxElevation * 100) / 100 : null,
    gps_accuracy_m: Math.round(s.avgGpsAccuracy * 10) / 10,
    gps_point_count: s.gpsPointCount,
    route_quality: s.routeQuality,
    tracking_mode: "gps",
    activity_status: "finished",
    unit_preference: "km",
  };

  try {
    const { data: inserted, error: wErr } = await supabase
      .from("workouts")
      .insert(workoutPayload)
      .select("id")
      .maybeSingle();

    if (wErr) return { success: false, error: wErr.message };
    if (!inserted) return { success: false, error: "No workout ID returned" };

    const workoutId = inserted.id;

    if (activity.points.length > 1) {
      const compressed = compressForStorage(activity.points, 500);
      const rows = compressed.map((p, i) => ({
        workout_id: workoutId,
        seq: i,
        lat: p.lat,
        lng: p.lng,
        ts: p.ts,
        accuracy_m: Math.round(p.accuracy * 10) / 10,
        speed_mps: Math.round(p.speed * 100) / 100,
        heading_deg: Math.round(p.heading * 100) / 100,
        altitude_m: p.altitude !== null ? Math.round(p.altitude * 100) / 100 : null,
        moving: p.moving,
      }));

      for (let i = 0; i < rows.length; i += 100) {
        const chunk = rows.slice(i, i + 100);
        const { error: rErr } = await supabase.from("workout_route_points").insert(chunk);
        if (rErr) break;
      }
    }

    LocalActivityStorage.markSynced(activity.id);
    await mergeWalkStepsToDaily(s.steps, s.calories, s.distance / 1000, startedAt.slice(0, 10), userId, deviceId);

    return { success: true, workoutId };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : "Sync failed" };
  }
}

export interface SyncPendingResult {
  synced: number;
  failed: number;
  total: number;
  errors: string[];
}

export async function syncPendingActivities(userId?: string): Promise<SyncPendingResult> {
  const unsynced = LocalActivityStorage.getUnsynced();
  let synced = 0;
  let failed = 0;
  const errors: string[] = [];

  for (const activity of unsynced) {
    const result = await syncActivity(activity, userId);
    if (result.success) {
      synced++;
    } else {
      failed++;
      if (result.error) errors.push(result.error);
    }
  }

  return { synced, failed, total: unsynced.length, errors };
}

function compressForStorage(points: GpsPoint[], maxPoints: number): GpsPoint[] {
  if (points.length <= maxPoints) return points;
  const step = Math.ceil(points.length / maxPoints);
  const compressed: GpsPoint[] = [];
  for (let i = 0; i < points.length; i += step) compressed.push(points[i]);
  const last = points[points.length - 1];
  if (compressed[compressed.length - 1].ts !== last.ts) compressed.push(last);
  return compressed;
}

export async function loadActivities(userId?: string): Promise<SavedActivityRow[]> {
  try {
    let q = supabase.from("workouts")
      .select("*")
      .order("started_at", { ascending: false })
      .limit(100);
    if (userId) q = q.eq("user_id", userId);
    else q = q.eq("device_id", getDeviceId());
    const { data } = await q;
    return (data ?? []) as unknown as SavedActivityRow[];
  } catch { return []; }
}

export async function loadRoutePoints(workoutId: string): Promise<GpsPoint[]> {
  try {
    const { data } = await supabase.from("workout_route_points")
      .select("lat,lng,ts,accuracy_m,speed_mps,heading_deg,altitude_m,moving")
      .eq("workout_id", workoutId)
      .order("seq", { ascending: true });
    return (data ?? []).map((r: Record<string, unknown>) => ({
      lat: Number(r.lat),
      lng: Number(r.lng),
      ts: Number(r.ts),
      accuracy: Number(r.accuracy_m ?? 0),
      speed: Number(r.speed_mps ?? 0),
      heading: Number(r.heading_deg ?? 0),
      altitude: r.altitude_m !== null ? Number(r.altitude_m) : null,
      moving: Boolean(r.moving),
    }));
  } catch { return []; }
}

export async function deleteActivity(workoutId: string): Promise<boolean> {
  try {
    await supabase.from("workout_route_points").delete().eq("workout_id", workoutId);
    await supabase.from("workouts").delete().eq("id", workoutId);
    return true;
  } catch { return false; }
}

export interface SavedActivityRow {
  id: string;
  date: string;
  started_at: string;
  ended_at: string | null;
  duration_secs: number;
  moving_secs: number;
  paused_secs: number;
  steps: number;
  distance_km: number;
  avg_speed_kmh: number;
  max_speed_kmh: number;
  avg_pace_sec_per_km: number;
  calories: number;
  elevation_gain_m: number;
  elevation_loss_m: number;
  min_elevation_m: number | null;
  max_elevation_m: number | null;
  gps_accuracy_m: number | null;
  gps_point_count: number;
  route_quality: string;
  tracking_mode: string;
  activity_status: string;
  unit_preference: string;
}

export type { ActivityStats, UnitPref };

// ─── Merge walk steps into daily_steps so the Steps tab reflects walking ──────
async function mergeWalkStepsToDaily(
  walkSteps: number,
  walkCalories: number,
  walkDistanceKm: number,
  date: string,
  userId?: string,
  deviceId?: string,
): Promise<void> {
  if (walkSteps <= 0) return;
  try {
    let query = supabase.from("daily_steps").select("id, steps, calories, distance_km").eq("date", date);
    if (userId) query = query.eq("user_id", userId);
    else query = query.eq("device_id", deviceId ?? getDeviceId());
    const { data: existing } = await query.maybeSingle();

    if (existing) {
      const newSteps = (existing.steps ?? 0) + walkSteps;
      await supabase.from("daily_steps").update({
        steps: newSteps,
        calories: (existing.calories ?? 0) + Math.round(walkCalories),
        distance_km: (existing.distance_km ?? 0) + Math.round(walkDistanceKm * 1000) / 1000,
        updated_at: new Date().toISOString(),
      }).eq("id", existing.id);
    } else {
      const payload: Record<string, unknown> = {
        date,
        steps: walkSteps,
        goal: 10000,
        calories: Math.round(walkCalories),
        distance_km: Math.round(walkDistanceKm * 1000) / 1000,
        active_secs: 0,
      };
      if (userId) {
        payload.user_id = userId;
      } else {
        payload.device_id = deviceId ?? getDeviceId();
      }
      await supabase.from("daily_steps").insert(payload);
    }
  } catch {}
}
